<template>
  <el-card v-loading="isLoading" shadow="always">
    <template #header>
      <el-row type="flex" justify="space-between" align="center">
        <el-col :md="12" :xs="16">
          <el-row type="flex" justify="start">
            <h4>Viagens de Entrega</h4>
          </el-row>
        </el-col>
        <el-col :md="12" :xs="8">
          <el-row type="flex" justify="end" align="center" class="header-actions">
            <el-select
              v-model="statusFilter"
              class="mt--5"
              size="medium"
              placeholder="Todos os status"
              clearable
              @change="handleStatusChange"
            >
              <el-option label="Criado" value="created" />
              <el-option label="Em viagem" value="departed" />
              <el-option label="Entregue" value="arrived" />
              <el-option label="Finalizado" value="finished" />
            </el-select>
            <el-button
              type="primary"
              icon="el-icon-plus"
              size="medium"
              @click="openTripModal(null)"
            />
          </el-row>
        </el-col>
      </el-row>
    </template>

    <el-table
      stripe
      :cell-style="() => 'text-align:center;'"
      :data="Trips"
      style="width: 100%; z-index: 0"
    >
      <el-table-column label="#" width="90">
        <template #default="{ row }">
          {{ row.uid?.slice(0, 8) }}
        </template>
      </el-table-column>
      <el-table-column label="veículo" min-width="130">
        <template #default="{ row }">
          {{ row.vehicle?.name || row.vehicle?.pane || "" }}
        </template>
      </el-table-column>
      <el-table-column label="motorista" min-width="150">
        <template #default="{ row }">
          {{ row.driver?.employee?.name || row.driver?.user?.name || "" }}
        </template>
      </el-table-column>
      <el-table-column label="pedidos" width="90">
        <template #default="{ row }">
          {{ row.orders?.length || 0 }}
        </template>
      </el-table-column>
      <el-table-column label="status" width="110">
        <template #default="{ row }">
          <el-tag :type="getStatusTag(row.status)">
            {{ getStatusLabel(row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="peso estimado" width="130">
        <template #default="{ row }">
          {{ formatWeight(row.estimated_products_weight) }}
        </template>
      </el-table-column>
      <el-table-column label="tara" width="110">
        <template #default="{ row }">
          {{ formatOptionalWeight(row.truck_tare_weight) }}
        </template>
      </el-table-column>
      <el-table-column label="peso de saída" width="130">
        <template #default="{ row }">
          {{ formatOptionalWeight(row.truck_departure_weight) }}
        </template>
      </el-table-column>
      <el-table-column label="peso real" width="120">
        <template #default="{ row }">
          {{ formatOptionalWeight(row.actual_loaded_weight) }}
        </template>
      </el-table-column>
      <el-table-column label="diferença" width="115">
        <template #default="{ row }">
          <el-tag
            v-if="hasWeightDifference(row)"
            :type="isAboveWeightThreshold(row) ? 'danger' : 'success'"
          >
            {{ formatPercent(row.weight_difference_percentage) }}
          </el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="criação" width="110">
        <template #default="{ row }">
          {{ dateFormatter.format(row.created_at) }}
        </template>
      </el-table-column>
      <el-table-column label="ações" min-width="180">
        <template #default="{ row }">
          <el-row type="flex" justify="end">
            <el-button-group>
              <el-button onlyIcon type="primary" circle @click="openDetails(row)">
                <i class="el-icon-view"></i>
              </el-button>
              <el-button
                onlyIcon
                type="primary"
                circle
                :disabled="row.status !== 'created'"
                @click="openTripModal(row)"
              >
                <i class="el-icon-edit"></i>
              </el-button>
              <el-popconfirm
                v-if="getNextStatus(row)"
                :title="`Alterar para ${getStatusLabel(getNextStatus(row))}?`"
                @confirm="advanceStatus(row)"
              >
                <template #reference>
                  <el-button onlyIcon type="success" circle>
                    <i class="el-icon-right"></i>
                  </el-button>
                </template>
              </el-popconfirm>
              <el-popconfirm
                v-if="row.status === 'created'"
                title="Remover viagem?"
                @confirm="deleteTrip(row)"
              >
                <template #reference>
                  <el-button onlyIcon type="danger" circle>
                    <i class="el-icon-delete"></i>
                  </el-button>
                </template>
              </el-popconfirm>
            </el-button-group>
          </el-row>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      :current-page="currentPage"
      :page-count="trips?.lastPage || 1"
      background
      layout="prev, pager, next"
      @update:current-page="handlePageChange"
    />

    <delivery-trip-modal
      :showModal="showTripModal"
      :trip="trip"
      :linkedOrderIds="LinkedOrderIds"
      @close-modal="showTripModal = false"
      @should-update="fetchTrips"
    />

    <el-dialog title="Detalhes da viagem" v-model="showDetailsModal" width="760px">
      <el-descriptions :column="2" border>
        <el-descriptions-item label="Status">
          {{ getStatusLabel(trip?.status) }}
        </el-descriptions-item>
        <el-descriptions-item label="Veículo">
          {{ trip?.vehicle?.name || trip?.vehicle?.pane || "" }}
        </el-descriptions-item>
        <el-descriptions-item label="Motorista">
          {{ trip?.driver?.employee?.name || "" }}
        </el-descriptions-item>
        <el-descriptions-item label="Pedidos">
          {{ trip?.orders?.length || 0 }}
        </el-descriptions-item>
        <el-descriptions-item label="Peso estimado">
          {{ formatWeight(trip?.estimated_products_weight) }}
        </el-descriptions-item>
        <el-descriptions-item label="Tara">
          {{ formatOptionalWeight(trip?.truck_tare_weight) }}
        </el-descriptions-item>
        <el-descriptions-item label="Peso real carregado">
          {{ formatOptionalWeight(trip?.actual_loaded_weight) }}
        </el-descriptions-item>
        <el-descriptions-item label="Diferença">
          {{ formatOptionalPercent(trip?.weight_difference_percentage) }}
        </el-descriptions-item>
      </el-descriptions>

      <el-alert
        v-if="isAboveWeightThreshold(trip)"
        type="warning"
        show-icon
        :closable="false"
        title="Divergência superior a 10%"
        class="weight-alert"
      />

      <el-table :data="trip?.orders || []" class="orders-table">
        <el-table-column prop="code" label="#" width="90" />
        <el-table-column prop="client.name" label="cliente" />
        <el-table-column label="produtos" width="100">
          <template #default="{ row }">
            {{ row.products?.length || 0 }}
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </el-card>
</template>

<script>
import DeliveryTripModal from "./modals/DeliveryTripModal.vue";
import DeliveryTripService from "../services/delivery_trips";
import { notifyError, notifySuccess } from "../utils/notifiers";
import {
  dateFormatter,
  numberFormatter,
  weightFormatter,
} from "../utils/formatters";

export default {
  name: "DeliveryTripsPage",
  components: { DeliveryTripModal },
  data: () => ({
    isLoading: true,
    trips: [],
    trip: null,
    showTripModal: false,
    showDetailsModal: false,
    currentPage: 1,
    statusFilter: "",
    fetchRepeater: null,
    dateFormatter,
  }),
  mounted() {
    this.fetchTrips();
    this.fetchRepeater = setInterval(() => this.fetchTrips(false), 5000);
  },
  unmounted() {
    clearInterval(this.fetchRepeater);
  },
  computed: {
    Trips() {
      return this.trips?.data || this.trips || [];
    },
    LinkedOrderIds() {
      return this.Trips.flatMap((trip) =>
        (trip.orders || []).map((order) => order.uid)
      );
    },
  },
  methods: {
    async fetchTrips(showLoading = true) {
      if (showLoading) this.isLoading = true;
      const { trips } = await DeliveryTripService().index({
        pagination: this.currentPage,
        limit: 15,
        ...(this.statusFilter ? { status: this.statusFilter } : {}),
      });

      if (trips) this.trips = trips;

      if (showLoading) this.isLoading = false;
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchTrips();
    },
    handleStatusChange() {
      this.currentPage = 1;
      this.fetchTrips();
    },
    async openTripModal(trip) {
      this.trip = trip ? await this.fetchTripDetails(trip) : null;
      this.showTripModal = true;
    },
    async openDetails(trip) {
      this.trip = await this.fetchTripDetails(trip);
      this.showDetailsModal = true;
    },
    async fetchTripDetails(trip) {
      const { trip: responseTrip } = await DeliveryTripService(trip.uid).get();
      return responseTrip || trip;
    },
    async advanceStatus(trip) {
      const nextStatus = this.getNextStatus(trip);
      const service = DeliveryTripService(trip.uid);
      const actions = {
        departed: () => service.depart(),
        arrived: () => service.arrive(),
        finished: () => service.finish(),
      };
      const { trip: updatedTrip, error } = await actions[nextStatus]();

      if (updatedTrip) {
        notifySuccess(updatedTrip.message);
        this.fetchTrips();
      } else notifyError(error?.message);
    },
    async deleteTrip(trip) {
      const { trip: deletedTrip, error } = await DeliveryTripService(
        trip.uid
      ).delete();

      if (deletedTrip) {
        notifySuccess(deletedTrip.message);
        this.fetchTrips();
      } else notifyError(error?.message);
    },
    getNextStatus(trip) {
      return {
        created: "departed",
        departed: "arrived",
        arrived: "finished",
      }[trip?.status];
    },
    getStatusLabel(status) {
      return (
        {
          created: "criado",
          departed: "em viagem",
          arrived: "entregue",
          finished: "finalizado",
        }[status] || status
      );
    },
    getStatusTag(status) {
      return (
        {
          created: "info",
          departed: "warning",
          arrived: "primary",
          finished: "success",
        }[status] || "info"
      );
    },
    hasWeightDifference(trip) {
      return (
        trip?.weight_difference_percentage !== null &&
        typeof trip?.weight_difference_percentage !== "undefined"
      );
    },
    isAboveWeightThreshold(trip) {
      return Number(trip?.weight_difference_percentage || 0) > 10;
    },
    formatWeight(value) {
      return `${weightFormatter.format(Number(value || 0))} kg`;
    },
    formatOptionalWeight(value) {
      if (value === null || typeof value === "undefined") return "-";
      return this.formatWeight(value);
    },
    formatPercent(value) {
      return `${numberFormatter.format(Number(value || 0))}%`;
    },
    formatOptionalPercent(value) {
      if (value === null || typeof value === "undefined") return "-";
      return this.formatPercent(value);
    },
  },
};
</script>

<style scoped>
.el-card {
  width: 100%;
}

.weight-alert,
.orders-table {
  margin-top: 12px;
}

.header-actions {
  gap: 8px;
}

.header-actions .el-select {
  width: 180px;
}

.el-pagination {
  margin-top: 16px;
}

.mt--5{
  margin-top: -5px !important;
}
</style>
